import { useState, useCallback } from 'react'
import { writeFetch, getSession } from '../api'
import { getRelationshipBasedOnSex, getOppositeRelationship, getBaseRelationshipType } from '../utils/relationshipMappings'
import { generateSmartFamilyRelationships, relationshipExists } from '../utils/familyLogic'

const formatNCBDate = (date) => {
  if (!date) return null;
  return new Date(date).toISOString().slice(0, 19).replace('T', ' ');
}

export const usePeople = () => {
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchPeople = useCallback(async () => {
    try {
      setLoading(true)
      const user = getSession()
      if (!user) return []
      const res = await writeFetch(`read/people_tracker_2024?owner_id=${user.id}&sort=name&order=asc&limit=1000`)
      const items = res.data || []
      const formatted = items.map(p => ({ ...p, is_family: p.is_family === 1 }))
      setPeople(formatted)
      return formatted
    } catch (err) {
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  const addPerson = useCallback(async (personData) => {
    setLoading(true)
    try {
      const user = getSession()
      const id = crypto.randomUUID()
      const { photoFile, ...cleanData } = personData;
      const payload = {
        ...cleanData,
        id,
        owner_id: user.id,
        is_family: personData.is_family ? 1 : 0,
        created_at: formatNCBDate(new Date()),
        updated_at: formatNCBDate(new Date())
      }

      if (photoFile) {
        const reader = new FileReader()
        payload.photo_url = await new Promise(r => {
          reader.onload = () => r(reader.result)
          reader.readAsDataURL(photoFile)
        })
      }

      await writeFetch('create/people_tracker_2024', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      const newPerson = { ...payload, is_family: payload.is_family === 1 }
      setPeople(prev => [...prev, newPerson])
      return newPerson
    } finally {
      setLoading(false)
    }
  }, [])

  const updatePerson = useCallback(async (id, personData) => {
    const { photoFile, ...cleanData } = personData;
    const payload = {
      ...cleanData,
      is_family: personData.is_family ? 1 : 0,
      updated_at: formatNCBDate(new Date())
    }

    if (photoFile) {
      const reader = new FileReader()
      payload.photo_url = await new Promise(r => {
        reader.onload = () => r(reader.result)
        reader.readAsDataURL(photoFile)
      })
    }

    await writeFetch(`update/people_tracker_2024/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })

    setPeople(prev => prev.map(p => p.id === id ? { ...p, ...payload, is_family: payload.is_family === 1 } : p))
    return { id, ...payload }
  }, [])

  const deletePerson = useCallback(async (id) => {
    await writeFetch(`delete/people_tracker_2024/${id}`, { method: 'DELETE' })
    setPeople(prev => prev.filter(p => p.id !== id))
  }, [])

  return { people, loading, fetchPeople, addPerson, updatePerson, deletePerson }
}

export const useRelationships = () => {
  const [relationships, setRelationships] = useState([])

  const fetchRelationships = useCallback(async () => {
    const user = getSession()
    if (!user) return []
    try {
      const res = await writeFetch(`read/relationships_tracker_2024?owner_id=${user.id}&limit=1000`)
      const items = res.data || []
      setRelationships(items)
      return items
    } catch (err) {
      return []
    }
  }, [])

  const syncAllFamilyRelationships = useCallback(async (allPeople, allRelationships) => {
    const user = getSession();
    if (!user) return { addedCount: 0 };

    console.log('[useData] Starting Deep Family Sync...');
    let totalAdded = 0;
    let currentRels = [...allRelationships];
    
    for (let pass = 0; pass < 3; pass++) {
      const newInferences = generateSmartFamilyRelationships(allPeople, currentRels);
      if (newInferences.length === 0) break;

      let passAdded = 0;
      for (const rel of newInferences) {
        if (!relationshipExists(rel.person_a_id, rel.person_b_id, currentRels)) {
          const pA = allPeople.find(p => String(p.id) === String(rel.person_a_id));
          const pB = allPeople.find(p => String(p.id) === String(rel.person_b_id));

          if (pA && pB) {
            const base = getBaseRelationshipType(rel.relationshipType);
            const opp = getOppositeRelationship(base, null);

            const payload = {
              id: crypto.randomUUID(),
              owner_id: user.id,
              person_a_id: String(rel.person_a_id),
              person_b_id: String(rel.person_b_id),
              relationship_type: getRelationshipBasedOnSex(opp, pA.sex),
              relationship_type_b: getRelationshipBasedOnSex(base, pB.sex),
              created_at: formatNCBDate(new Date()),
              updated_at: formatNCBDate(new Date())
            };

            try {
              await writeFetch('create/relationships_tracker_2024', {
                method: 'POST',
                body: JSON.stringify(payload)
              });
              currentRels.push(payload);
              passAdded++;
              totalAdded++;
            } catch (err) {
              console.error('[useData] Sync error:', err);
            }
          }
        }
      }
      if (passAdded === 0) break;
    }

    if (totalAdded > 0) {
      await fetchRelationships();
    }
    return { addedCount: totalAdded };
  }, [fetchRelationships]);

  const addRelationship = useCallback(async (currentPersonId, otherPersonId, relType, allPeople = [], allRelationships = []) => {
    const user = getSession();
    if (!user) throw new Error('Not logged in.');

    const pA = allPeople.find(p => String(p.id) === String(currentPersonId));
    const pB = allPeople.find(p => String(p.id) === String(otherPersonId));
    if (!pA || !pB) throw new Error('Profile missing.');

    const baseType = getBaseRelationshipType(relType);
    const oppBase = getOppositeRelationship(baseType, null);

    const payload = {
      id: crypto.randomUUID(),
      owner_id: user.id,
      person_a_id: String(currentPersonId),
      person_b_id: String(otherPersonId),
      relationship_type: getRelationshipBasedOnSex(oppBase, pA.sex),
      relationship_type_b: getRelationshipBasedOnSex(baseType, pB.sex),
      created_at: formatNCBDate(new Date()),
      updated_at: formatNCBDate(new Date())
    };

    await writeFetch('create/relationships_tracker_2024', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    const updatedRelationships = [...allRelationships, payload];
    setRelationships(updatedRelationships);

    const syncRes = await syncAllFamilyRelationships(allPeople, updatedRelationships);
    return { success: true, addedCount: syncRes.addedCount };
  }, [syncAllFamilyRelationships]);

  const updateRelationship = useCallback(async (id, data, currentPersonId, allPeople = []) => {
    const user = getSession();
    if (!user) throw new Error('Not logged in.');

    const existingRel = relationships.find(r => r.id === id);
    if (!existingRel) throw new Error('Relationship not found.');

    const isA = String(existingRel.person_a_id) === String(currentPersonId);
    
    const pA = allPeople.find(p => String(p.id) === String(existingRel.person_a_id));
    const pB = allPeople.find(p => String(p.id) === String(existingRel.person_b_id));

    if (!pA || !pB) throw new Error('Profile missing.');

    const baseType = getBaseRelationshipType(data.relationshipType);
    const oppBase = getOppositeRelationship(baseType, null);

    const payload = {
      updated_at: formatNCBDate(new Date())
    };

    if (isA) {
      payload.relationship_type = getRelationshipBasedOnSex(oppBase, pA.sex);
      payload.relationship_type_b = getRelationshipBasedOnSex(baseType, pB.sex);
    } else {
      payload.relationship_type = getRelationshipBasedOnSex(baseType, pB.sex);
      payload.relationship_type_b = getRelationshipBasedOnSex(oppBase, pA.sex);
    }

    await writeFetch(`update/relationships_tracker_2024/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });

    const updatedRels = relationships.map(r => r.id === id ? { ...r, ...payload } : r);
    setRelationships(updatedRels);

    const syncRes = await syncAllFamilyRelationships(allPeople, updatedRels);
    return { success: true, addedCount: syncRes.addedCount };
  }, [relationships, syncAllFamilyRelationships]);

  const cleanupDatabase = useCallback(async (allPeople) => {
    const user = getSession();
    if (!user) return { cleaned: 0 };
    try {
      const allRels = await fetchRelationships();
      const peopleIds = new Set(allPeople.map(p => String(p.id)));
      
      const orphanedRels = allRels.filter(r => !peopleIds.has(String(r.person_a_id)) || !peopleIds.has(String(r.person_b_id)));
      
      const uniquePairs = new Set();
      const duplicateRels = [];
      const sortedRels = [...allRels].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      for (const rel of sortedRels) {
        const ids = [String(rel.person_a_id), String(rel.person_b_id)].sort();
        const pairKey = `${ids[0]}_${ids[1]}`;
        if (uniquePairs.has(pairKey)) {
          duplicateRels.push(rel);
        } else {
          uniquePairs.add(pairKey);
        }
      }

      const toDelete = [...new Set([...orphanedRels, ...duplicateRels].map(r => r.id))];
      for (const id of toDelete) {
        await writeFetch(`delete/relationships_tracker_2024/${id}`, { method: 'DELETE' });
      }
      if (toDelete.length > 0) await fetchRelationships();
      return { cleaned: toDelete.length };
    } catch (err) {
      console.error("Cleanup error:", err);
      throw err;
    }
  }, [fetchRelationships]);

  const deleteAllRelationships = useCallback(async () => {
    const user = getSession();
    if (!user) return { deleted: 0 };
    try {
      const allRels = await fetchRelationships();
      for (const rel of allRels) {
        await writeFetch(`delete/relationships_tracker_2024/${rel.id}`, { method: 'DELETE' });
      }
      setRelationships([]);
      return { deleted: allRels.length };
    } catch (err) {
      console.error("Delete all relationships error:", err);
      throw err;
    }
  }, [fetchRelationships]);

  /**
   * deleteRelationship - Deletes a relationship AND all duplicate rows
   * for the same pair of people, ensuring it's removed from both sides.
   */
  const deleteRelationship = useCallback(async (id) => {
    // 1. Find the target relationship to get the pair of people
    const targetRel = relationships.find(r => r.id === id);
    
    if (targetRel) {
      const personAId = String(targetRel.person_a_id);
      const personBId = String(targetRel.person_b_id);

      // 2. Find ALL relationship rows between these two people (duplicates)
      const duplicates = relationships.filter(r => {
        const rA = String(r.person_a_id);
        const rB = String(r.person_b_id);
        return (rA === personAId && rB === personBId) ||
               (rA === personBId && rB === personAId);
      });

      console.log(`[useData] Deleting relationship ${id} between ${personAId} and ${personBId}. Found ${duplicates.length} row(s) to remove.`);

      // 3. Delete ALL duplicate rows from the database
      for (const dup of duplicates) {
        await writeFetch(`delete/relationships_tracker_2024/${dup.id}`, { method: 'DELETE' });
      }

      // 4. Remove ALL duplicates from local state
      const duplicateIds = new Set(duplicates.map(d => d.id));
      setRelationships(prev => prev.filter(r => !duplicateIds.has(r.id)));
    } else {
      // Fallback: if we can't find the rel in state, just delete by ID
      await writeFetch(`delete/relationships_tracker_2024/${id}`, { method: 'DELETE' });
      setRelationships(prev => prev.filter(r => r.id !== id));
    }
  }, [relationships])

  return { relationships, fetchRelationships, addRelationship, updateRelationship, deleteRelationship, cleanupDatabase, syncAllFamilyRelationships, deleteAllRelationships }
}